import { useHasPlaybackContext } from '@app/core/playback/hooks/usePlayback';
import { ExclusivePlaybackMode } from '@app/core/playback/store/base-playback-slice';
import { Station } from '@app/core/types/stations';
import { PlaybackIconBtnV2PlayingAction } from '@mi-front/ui';
import { SegmentsAudioPlaybackProvider } from '../SegmentsAudioPlaybackProvider';
import { RangeWaveformPlayerContent } from './RangeWaveformPlayerContent';

const DEFAULT_WAVEFORM_HEIGHT = 64;

export interface RangeWaveformPlayerProps {
  station: Station;
  /** Range start (unix ms). */
  from: number;
  /** Range end (unix ms). */
  to: number;
  /** Optional identifier for this playback instance. */
  playbackId?: string;
  exclusivePlaybackMode?: ExclusivePlaybackMode;
  /**
   * While playing: `pause` (default) keeps position; `stop` resets to range start.
   */
  playingAction?: PlaybackIconBtnV2PlayingAction;
  /** Called when playback starts or seeks so sibling players can pause. */
  onPlayStarted?: () => void;
  /** Waveform graph height in px (axis is additional when shown). */
  height?: number;
  /** When true, start playback once segments are available. */
  autoPlay?: boolean;
}

/**
 * Compact reusable audio player for a station time range: waveform, advancing
 * playhead, click-to-seek, and play / pause-or-stop. Creates its own playback
 * provider when none exists in the parent tree.
 */
export function RangeWaveformPlayer({
  station,
  from,
  to,
  playbackId,
  exclusivePlaybackMode = 'pause',
  playingAction = 'pause',
  onPlayStarted,
  height = DEFAULT_WAVEFORM_HEIGHT,
  autoPlay = false,
}: RangeWaveformPlayerProps) {
  const hasParentContext = useHasPlaybackContext();

  const content = (
    <RangeWaveformPlayerContent
      station={station}
      from={from}
      to={to}
      height={height}
      playingAction={playingAction}
      onPlayStarted={onPlayStarted}
      autoPlay={autoPlay}
    />
  );

  if (hasParentContext) {
    return content;
  }

  return (
    <SegmentsAudioPlaybackProvider
      key={`${from}-${to}`}
      station={station}
      playbackRange={{ from, to }}
      playbackId={playbackId}
      exclusivePlaybackMode={exclusivePlaybackMode}
    >
      {content}
    </SegmentsAudioPlaybackProvider>
  );
}
